/// <amd-module name="SearchUI/Components/SearchPickList/SearchPickList" />

import * as React from "react";
import * as ReactDOM from "react-dom";
import { Tooltip } from "VSSUI/TooltipEx";
import { css, KeyCode } from "VSSUI/Util";
import { getId } from "VSS/Core/Util/Object";
import { ISearchPickListProps, ISearchPickListItem } from "SearchUI/Components/SearchPickList/SearchPickList.Props";
import { SearchPickListMenu } from "SearchUI/Components/SearchPickList/SearchPickListMenu";
import { ExpandableSearchPickListMenu } from "SearchUI/Components/SearchPickList/ExpandableSearchPickListMenu";
import { CalloutComponent } from "SearchUI/Components/Callout";
import { Icon } from 'VSSUI/Icon';
import { Location } from "VSSUI/Utilities/Position";
import { format } from "VSS/Core/Util/String";
import * as Resources from "SearchUI/Search.Resources.SearchUI";

export interface ISearchPickListState {
    dropdownOpen: boolean;

    checkedItems: ISearchPickListItem[];

    calloutShown: boolean;
}

/**
 * Margin added to props.width to compute the width of the pickList--Button.
 * It takes into account the additional margin of the component div/spans.
 * If there is a change in width of any of the div/span with pickLilst--Button, this values needs updation.
 */
const PickListButtonAdditonalWidth = 43;

export class SearchPickList extends React.Component<ISearchPickListProps, ISearchPickListState> {
    private _pickList: HTMLElement;
    private _menuButton: HTMLElement;
    private _helpIconRef: React.RefObject<HTMLElement>;

    constructor(props: ISearchPickListProps) {
        super(props);
        const checkedItems = props.items.filter(f => f.selected);
        this.state = {
            dropdownOpen: false,
            checkedItems: checkedItems,
            calloutShown: false
        };

        this._helpIconRef = React.createRef<HTMLDivElement>();
    }

    public render(): JSX.Element {
        const compactMode = this.props.compactMode && this.props.items.length === 1;
        const { activeItemNamesOverflown, displayString, entireString } =
            compactMode ?
                {
                    activeItemNamesOverflown: false,
                    displayString: this.props.items[0].name,
                    entireString: this.props.items[0].name
                } :
                this.getDisplayInformation(this.state.checkedItems, this.props.displayName, this.props.lightWeightDisplay);
        const calloutValues = compactMode ? this.props.compactModeCalloutProps : this.props.calloutProps;

        const menuButtonId: string = getId("search-PickList-Button-"),
            filterDropdownId: string = getId("search-PickList-Menu-"),
            calloutId = getId("search-Filter--CalloutId"),
            attributes = this.getAttributes(menuButtonId, filterDropdownId, calloutId, compactMode),
            disabledInfoId = getId("search-DisabledFilter-Info-");

        const menuButton = <div {...attributes}
            style={{
                width: `${this.props.width + PickListButtonAdditonalWidth}px`
            }}>
            <span
                id={menuButtonId}
                className={css("search-PickListLabel--container", compactMode && "compact-mode")}
                style={{
                    width: `${this.props.width}px`
                }}
                role="textbox"
                aria-label={menuButtonId}>
                <span
                    className={css("search-PickList--label", !this.props.enabled && "is-disabled", compactMode && "compact-mode")}>
                    {this.props.displayName + ": "}</span>
                <span>{displayString}</span>
            </span>
            {
                // render "help" icon
                (!this.props.enabled || compactMode) && !this.props.hideInfoIcon &&
                <span
                    ref={this._helpIconRef}
                    id={disabledInfoId}
                    className={css("disabled-Info--icon")} />
            }
            <Icon
                className={css("drop-button", !this.props.enabled && "is-disabled", compactMode && "compact-mode")}
                ariaLabel={this.props.displayName + " " + Resources.ComboBoxDropButtonText}
                iconName="ChevronDown"
                role="button" />
        </div>;

        const searchPickListMenuProps = {
            id: filterDropdownId,
            items: this.props.items,
            allItemDisplayName: this.props.allItemDisplayName,
            searchTextPlaceholder: this.props.searchTextPlaceholder,
            onRefineItems: this.props.onRefineItems,
            onSelectionChanged: this.onSelectionChanged,
            onGetFooterMessage: this.props.onGetFooterMessage,
            onDismiss: this.closeDropdown,
            disableAnnounce: this.props.disableAnnounce,
            dataLoading: this.props.dataLoading
        }

        return (
            <div>
                <div
                    className={css("search-PickList", this.props.className)}
                    aria-describedby={calloutId}
                    ref={((pickListElement) => {
                        this._pickList = pickListElement
                    }).bind(this)} >
                    {
                        // wrap button within tooltip host, if filter is enabled and not compact mode.
                        this.props.enabled && !compactMode && activeItemNamesOverflown
                            ? <Tooltip
                                anchorOrigin={{ horizontal: Location.center, vertical: Location.start }}
                                showOnFocus={true}
                                text={entireString}
                                tooltipOrigin={{ horizontal: Location.center, vertical: Location.end }}
                            >
                                {menuButton}
                            </Tooltip>
                            : menuButton
                    }
                    {
                        this.state.dropdownOpen ?
                            !this.props.isExpandable ?
                                <SearchPickListMenu
                                    {...searchPickListMenuProps} />
                                : <ExpandableSearchPickListMenu
                                    {...searchPickListMenuProps}
                                    menuMaxWidth={this.props.menuMaxWidth} />
                            : null
                    }
                </div>
                {
                    // Render call out component
                    this.state.calloutShown &&
                    calloutValues &&
                    <CalloutComponent
                        targetElement={this._helpIconRef.current}
                        title={calloutValues["title"]}
                        content={calloutValues["content"]}
                        id={calloutId} />
                }
            </div>
        );
    }

    public componentWillReceiveProps(newProps: ISearchPickListProps): void {
        const checkedItems = newProps.items.filter(i => i.selected);
        // Set the callout state so that is hidden when the control gets enabled after getting the search response.
        this.setState({ checkedItems: checkedItems, calloutShown: false, dropdownOpen: this.state.dropdownOpen && newProps.enabled });
    }

    public focus(): void {
        this._menuButton.focus();
    }

    /**
     * Invoked when a filter is modified in the dropdown.
     * @param checkedItems - List of items which are selected.
     */
    private onSelectionChanged = (checkedItems: ISearchPickListItem[]): void => {
        if (this.props.onSelectionChanged) {
            // fire on selection change.
            const name = this.props.name;

            this.setState({ checkedItems: checkedItems });

            this.props.onSelectionChanged(name, checkedItems);
        }
    }

    private closeDropdown = (evt): void => {
        if (evt.type === "blur") {
            // On blur see if the related target which recieves the focus lies within the DOM tree of the MultiSelectMenu.
            // If yes, it implies the multiselect menu or its child recieved the focus, in which case we don't want the dropdown 
            // to go away.
            const relatedTarget = evt.relatedTarget || document.activeElement;
            if (this._pickList && !this._pickList.contains(relatedTarget)) {
                this.setState({ dropdownOpen: false });
            }
        }
        else if (evt.type === "keydown" && evt.keyCode === KeyCode.escape) {
            this.setState({ dropdownOpen: false });
            this.focus();
        }
    }

    private onDropdownButtonClick = (): void => {
        this.setState({ dropdownOpen: !this.state.dropdownOpen });
        if (this.props.onClick) {
            this.props.onClick();
        }
    }

    private onKeyDown = (evt): void => {
        if (evt.keyCode === KeyCode.enter || evt.keyCode === KeyCode.downArrow) {
            // pressing enter or arrow down is same as clicking the button to bring up the dropdown menu.
            this.onDropdownButtonClick();
        }
    }

    /**
     * Handle Keyboard navigation when focus is on disabled filter bar
     * @param evt
     */
    private onFilterDisabledKeyDown = (evt): void => {
        if (evt.keyCode === KeyCode.escape) {
            this.triggerCallout(false);
        }
    }

    private triggerCallout = (showCallout: boolean): void => {
        if (this.props.triggerCallout) {
            this.props.triggerCallout(showCallout);
        }
        this.setState({ calloutShown: showCallout });
    }

    private getAttributes(menuButtonId: string, filterDropdownId: string, calloutId: string, compactMode: boolean): any {
        let attributes = {
            "ref": (element) => { this._menuButton = element },
            "className": css("search-PickList--button", !this.props.enabled && "is-disabled"),
            "role": "combobox",
            "tabIndex": (!this.props.enabled ? -1 : 0),
            "aria-disabled": !this.props.enabled,
            "aria-haspopup": true,
            "aria-expanded": this.state.dropdownOpen,
            "aria-labelledby": menuButtonId,
            "aria-autocomplete": "list"
        };

        if (this.state.dropdownOpen) {
            attributes["aria-owns"] = filterDropdownId;
            attributes["aria-controls"] = filterDropdownId;
        }

        // Bind to onClick, and OnkeyDown events only when the filter is enabled and not compact mode.
        if (this.props.enabled && !compactMode) {
            attributes["onClick"] = this.onDropdownButtonClick;
            attributes["onKeyDown"] = this.onKeyDown;
            attributes["onBlur"] = this.closeDropdown;
        }
        else {
            attributes["onFocus"] = () => this.triggerCallout(true);
            attributes["onBlur"] = () => this.triggerCallout(false);
            attributes["onMouseOver"] = () => this.triggerCallout(true);
            attributes["onMouseLeave"] = () => this.triggerCallout(false);
            attributes["aria-describedby"] = calloutId;
            attributes["disabled"] = true;
            attributes["onKeyDown"] = this.onFilterDisabledKeyDown;
        }

        return attributes;
    }

    private getDisplayInformation(selectedItems: ISearchPickListItem[], filterLabel: string, lightWeightDisplay?: boolean): any {
        if (!lightWeightDisplay) {

            const remainingSpaceCap = 60,
                maxWidth = this.props.width;

            let $div = document.createElement("div");

            $div.setAttribute(
                "style",
                `position: absolute;
                top: -5000px; 
                left: -5000px; 
                font-size: 14px`
            );

            document.body.appendChild($div);

            // sort items from longest to shortest as we want to show longer names first.
            selectedItems.sort((a, b) => { return b.name.length - a.name.length });

            const selectedNames = selectedItems.map(item => item.name).join(", ");

            $div.innerHTML = filterLabel + ": " + selectedNames;

            let overflowOccurred = $div.clientWidth > maxWidth,
                moveToNextItem: boolean = true,
                itemsLength = selectedItems.length,
                fullLabel = overflowOccurred ? "" : selectedNames;

            // once overflow is detected try to get the label showing maximum number of characters along with +more count.
            for (let i = 0; overflowOccurred && moveToNextItem && i < itemsLength; i++) {
                const itemName: string = selectedItems[i].name,
                    remainingCount = itemsLength - (i + 1);

                // set current item name in the div and see after placing the text we have enough space let to accomodate
                // "<at least 2 chars>... (+more)" format string. If not we truncate the current item name and append it with
                // more count as the final label, other wise we proceed onto the next item name.
                $div.innerHTML = filterLabel + ": " + fullLabel + itemName;
                moveToNextItem = (maxWidth - $div.clientWidth) >= remainingSpaceCap;

                if (moveToNextItem) {
                    fullLabel += itemName + ", ";
                }
                else if (remainingCount <= 0) {
                    fullLabel = itemName;
                }
                else {
                    const itemNameLength = itemName.length;
                    let itemLabel = "";

                    // start from end of the string, and try accomodating the string along with +more count.
                    // Break as soon as the length of the constructed label adjusts within the limits.
                    for (let j = itemNameLength; j > 0; j--) {
                        const subtring = itemName.substr(0, j),
                            // If the label is able to contain the full item name, there is no need to show ellipsis.
                            separator = (j === itemNameLength) ? " " : "... ";

                        itemLabel = subtring + separator + "(+" + remainingCount.toString() + ")";

                        $div.innerHTML = filterLabel + ": " + fullLabel + itemLabel;

                        // break if the the label is long enough to be contained within the space we have.
                        if ($div.clientWidth < maxWidth) {
                            break;
                        }
                    }

                    fullLabel += itemLabel;
                }
            }

            document.body.removeChild($div);

            return {
                activeItemNamesOverflown: overflowOccurred,
                displayString: fullLabel || this.props.allItemLabel,
                entireString: selectedNames
            };
        } else {
            // without measure just create minimal label of the form `AzureDevOps(+2)` etc - the logic used in cases otherwise hits page perf.
            let fullLabel = this.props.allItemLabel;
            const tooltipText = selectedItems.map(item => item.name).join(", ");

            if (selectedItems.length > 1) {
                fullLabel = format("{0}(+{1})", selectedItems[0].name, selectedItems.length - 1);
            } else if (selectedItems.length === 1) {
                fullLabel = selectedItems[0].name
            }

            return {
                //ToDo: As of now not allowing tooltip in this mode, as upon blur "tooltip" component calls child's onBlur method - which needs more handling.
                activeItemNamesOverflown: false,
                displayString: fullLabel,
                entireString: tooltipText
            }
        }
    }
}
